import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Card module
 */
export default class Lightbox extends MessageBus {
  /**
   * Constructor function of the Lightbox class.
   * @param {HTMLElement} element - The module's container
   */
  constructor(element) {
    super();

    this.element = element;
    this.lightbox = this.element.querySelector(`.${CLASS_NAMES.LIGHTBOX}`);
    this.lightbox_button = this.element.querySelector(
      `.${CLASS_NAMES.LIGHTBOX_BTN}`
    );
    this.lightbox_circle = this.element.querySelector(
      CLASS_NAMES.LIGHTBOX_CIRCLE_SVG
    );
    this.cookieName = `${SELECTORS.CARE_LIGHTBOX}`;
    this.cookieExpiry = parseInt(this.element.dataset.cookieExpiry, 10);
    this.hash = this.element.dataset.hash;
    this.KEY = `${CLASS_NAMES.GEO_LOCATION_POPUP_HIDDEN}`;

    // Add these handlers
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);

    this.init();
  }

  /**
   * Initializes the view by calling necessary functions
   * @returns {object} Lightbox A reference to the current instance of the class
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    this.checkAndShowLightbox();
    return this;
  }

  /**
   * Cache DOM References
   * @returns {object} Lightbox
   */
  cacheDomReferences() {
    this.closeButtons = this.element.querySelectorAll(
      `.${CLASS_NAMES.LIGHTBOX_CLOSE}`
    );
    return this;
  }

  /**
   * Setup event handlers
   * @returns {object} Lightbox
   */
  setupHandlers() {
    this.closeHandler = this.closeLightbox.bind(this);

    // Add hover events if elements exist
    if (this.lightbox_button && this.lightbox) {
      this.lightbox_button.addEventListener(
        EVENTS.MOUSEENTER,
        this.handleMouseEnter
      );
      this.lightbox_button.addEventListener(
        EVENTS.MOUSELEAVE,
        this.handleMouseLeave
      );
    }

    return this;
  }

  /**
   * Enable event listeners
   * @returns {object} Lightbox
   */
  enable() {
    this.closeButtons.forEach((btn) =>
      btn.addEventListener(EVENTS.CLICK, this.closeHandler)
    );
    return this;
  }

  /**
   * Checks if the lightbox should be displayed based on cookies and session storage
   */
  checkAndShowLightbox() {
    if (Lightbox.getCookie(this.cookieName) !== this.hash) {
      if (
        !sessionStorage.getItem(this.KEY) ||
        sessionStorage.getItem(this.KEY) === null
      ) {
        this.showLightbox();
      }
    }
  }

  /**
   * Show the lightbox
   */
  showLightbox() {
    this.element.classList.add(`${SELECTORS.LIGHTBOX_VISIBLE}`);
  }

  /**
   * Close the lightbox and set a cookie
   */
  closeLightbox() {
    this.element.classList.remove(`${SELECTORS.LIGHTBOX_VISIBLE}`);
    Lightbox.setCookie(this.cookieName, this.hash, this.cookieExpiry);
  }

  /**
   * Sets a cookie with a given name, value, and expiration time.
   * @param {string} name - The name of the cookie.
   * @param {string} value - The value to store in the cookie.
   * @param {number} days - Number of days before the cookie expires.
   */
  static setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value}${expires}; path=/`;
  }

  /**
   * Retrieves the value of a cookie by name.
   * @param {string} name - The name of the cookie to retrieve.
   * @returns {string|null} The value of the cookie, or null if not found.
   */
  static getCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
  }

  /**
   * Handle mouse enter event
   */
  handleMouseEnter() {
    this.lightbox.classList.add(CLASS_NAMES.LIGHTBOX_CIRCLE_SCALE);
  }

  /**
   * Handle mouse leave event
   */
  handleMouseLeave() {
    this.lightbox.classList.remove(CLASS_NAMES.LIGHTBOX_CIRCLE_SCALE);
  }

  /**
   * Cleanup in case component is destroyed
   */
  destroy() {
    if (this.lightbox_button && this.lightbox) {
      this.lightbox_button.removeEventListener(
        EVENTS.MOUSEENTER,
        this.handleMouseEnter
      );
      this.lightbox_button.removeEventListener(
        EVENTS.MOUSELEAVE,
        this.handleMouseLeave
      );
    }
  }
}
